/**
 * BookMediaLibrary — Upload Draft "Book media library" panel (iter-25/26).
 *
 * Lists every hero image currently attached to a chapter in this book,
 * as a grid of thumbnails. Each item exposes:
 *   - "Move to chapter" (moves the media row without re-uploading or
 *     re-generating — cheap swap; source chapter loses its hero).
 *   - "Duplicate to chapter" (iter-26 — copies the bytes into a NEW
 *     sidecar row for the target chapter; source chapter keeps its hero).
 *   - "Remove" (deletes the media row + clears the chapter's hero — now
 *     confirmed via a shadcn AlertDialog instead of window.confirm).
 *
 * Backed by:
 *   GET    /api/books/:id/media
 *   POST   /api/books/:id/media/:media_id/attach/:target_source_index
 *   POST   /api/books/:id/media/:media_id/duplicate/:target_source_index
 *   DELETE /api/books/:id/chapters/:source_index/hero-image
 *
 * Auto-reloads whenever the parent ``book`` prop changes so mutations in
 * other panels (uploads, generates, batch) refresh the library.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, ArrowRightLeft, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function BookMediaLibrary({ book, onBookUpdated }) {
  const [state, setState] = useState({ loading: true, items: [], available_chapters: [] });
  const [busyId, setBusyId] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null); // item to confirm removing

  const load = useCallback(async () => {
    if (!book?.id) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/books/${book.id}/media`);
      setState({ loading: false, ...data });
    } catch (err) {
      toast.error(formatApiError(err));
      setState((s) => ({ ...s, loading: false }));
    }
  }, [book?.id]);

  // Reload on book change — captures uploads/deletes from other panels.
  useEffect(() => { load(); }, [load, book?.updated_at]);

  const attach = useCallback(async (item, targetSidx) => {
    if (!targetSidx || Number(targetSidx) === item.chapter_source_index) return;
    setBusyId(item.media_id);
    try {
      const { data } = await api.post(
        `/books/${book.id}/media/${item.media_id}/attach/${targetSidx}`);
      onBookUpdated?.(data);
      toast.success(`Moved to "${targetTitleFor(state, targetSidx)}"`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  }, [book.id, onBookUpdated, load, state]);

  const duplicate = useCallback(async (item, targetSidx) => {
    if (!targetSidx || Number(targetSidx) === item.chapter_source_index) return;
    setBusyId(item.media_id);
    try {
      const { data } = await api.post(
        `/books/${book.id}/media/${item.media_id}/duplicate/${targetSidx}`);
      onBookUpdated?.(data);
      toast.success(`Duplicated to "${targetTitleFor(state, targetSidx)}"`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  }, [book.id, onBookUpdated, load, state]);

  const confirmRemove = useCallback(async () => {
    const item = pendingRemoval;
    if (!item) return;
    setBusyId(item.media_id);
    setPendingRemoval(null);
    try {
      const { data } = await api.delete(
        `/books/${book.id}/chapters/${item.chapter_source_index}/hero-image`);
      onBookUpdated?.(data);
      toast.success("Removed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  }, [pendingRemoval, book.id, onBookUpdated, load]);

  const items = state.items || [];
  // Only render the panel when there's at least one hero in the book —
  // otherwise it just clutters the page with a "no media" empty state.
  if (!state.loading && items.length === 0) return null;

  return (
    <div className="mt-6 border border-[color:var(--border)] bg-white" data-testid="book-media-library">
      <div className="border-b border-[color:var(--border)] px-5 py-4">
        <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">§ Book media library</p>
        <h3 className="text-lg font-semibold mt-1">Chapter hero images</h3>
        <p className="text-xs text-[color:var(--text-muted)] mt-1">
          {items.length} image{items.length === 1 ? "" : "s"} attached · re-use across chapters without re-uploading.
        </p>
      </div>
      {state.loading ? (
        <div className="p-4 text-sm text-[color:var(--text-muted)]" data-testid="media-library-loading">Loading…</div>
      ) : (
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
             data-testid="media-library-grid">
          {items.map((item) => (
            <MediaItem
              key={item.media_id}
              item={item}
              state={state}
              busy={busyId === item.media_id}
              onMove={(sidx) => attach(item, sidx)}
              onDuplicate={(sidx) => duplicate(item, sidx)}
              onRemove={() => setPendingRemoval(item)}
            />
          ))}
        </div>
      )}

      <AlertDialog open={!!pendingRemoval} onOpenChange={(o) => { if (!o) setPendingRemoval(null); }}>
        <AlertDialogContent data-testid="media-library-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this hero image?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRemoval
                ? `The hero image for "${pendingRemoval.chapter_title}" will be permanently deleted. This cannot be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="media-library-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemove}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
              data-testid="media-library-remove-confirm"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MediaItem({ item, state, busy, onMove, onDuplicate, onRemove }) {
  const targets = (state.available_chapters || []).filter(
    (c) => c.source_index !== item.chapter_source_index
  );
  return (
    <div
      className="border border-[color:var(--border)] bg-[color:var(--canvas)] p-3"
      data-testid={`media-library-item-${item.media_id}`}
      data-chapter-source-index={item.chapter_source_index}
    >
      <div className="aspect-[3/2] bg-white flex items-center justify-center overflow-hidden">
        <img
          src={item.url}
          alt=""
          className="w-full h-full object-cover"
          data-testid={`media-library-thumb-${item.media_id}`}
          loading="lazy"
        />
      </div>
      <div className="mt-2 min-w-0">
        <p className="text-sm font-medium truncate" title={item.chapter_title}>{item.chapter_title}</p>
        <p className="text-[11px] text-[color:var(--text-muted)]">
          {item.source === "ai" ? "AI · Nano Banana" : "Upload"}
          {" · "}
          {formatSize(item.size)}
        </p>
        {item.prompt && (
          <p className="text-[11px] italic text-[color:var(--text-muted)] truncate"
             title={item.prompt}>
            "{item.prompt}"
          </p>
        )}
      </div>
      <div className="mt-3 space-y-2">
        <label className="text-xs flex items-center gap-1">
          <ArrowRightLeft className="w-3.5 h-3.5 shrink-0" />
          <select
            defaultValue=""
            onChange={(e) => { const v = e.target.value; e.target.value = ""; onMove(Number(v)); }}
            disabled={busy}
            className="text-xs border border-[color:var(--border)] bg-white px-2 py-1 flex-1 min-w-0"
            data-testid={`media-library-attach-${item.media_id}`}
          >
            <option value="" disabled>Move to chapter…</option>
            {targets.map((c) => (
              <option key={c.source_index} value={c.source_index}>{c.title}</option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-2">
          <label className="text-xs flex items-center gap-1 flex-1 min-w-0">
            <Copy className="w-3.5 h-3.5 shrink-0" />
            <select
              defaultValue=""
              onChange={(e) => { const v = e.target.value; e.target.value = ""; onDuplicate(Number(v)); }}
              disabled={busy}
              className="text-xs border border-[color:var(--border)] bg-white px-2 py-1 flex-1 min-w-0"
              data-testid={`media-library-duplicate-${item.media_id}`}
            >
              <option value="" disabled>Duplicate to chapter…</option>
              {targets.map((c) => (
                <option key={c.source_index} value={c.source_index}>{c.title}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            className="btn-ghost text-xs inline-flex items-center gap-1 text-red-700 hover:text-red-800 shrink-0"
            title="Remove from this chapter and delete the media"
            data-testid={`media-library-remove-${item.media_id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function targetTitleFor(state, sidx) {
  const c = (state.available_chapters || []).find((c) => c.source_index === Number(sidx));
  return c?.title || `chapter #${sidx}`;
}

function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
